import React, { createContext, useMemo } from 'react';
import { GroupBase, components as SelectComponents, ValueContainerProps } from 'react-select';
import { Box } from '@tanium/react-box';
import { useDiscreteOverflow } from '@tanium/react-discrete-overflow';
import { css } from '@tanium/react-emotion';

export interface MultiCollapsibleContextType {
  /**
   * The maximum number of pills to display in the collapsible select.
   */
  maxPills?: number;
}

export const MultiCollapsibleContext = createContext<MultiCollapsibleContextType>({});

export const createValueContainer =
  <Option, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>(
    isMultiCollapsible: boolean,
  ) =>
  (props: ValueContainerProps<Option, IsMulti, Group>) => {
    const {
      selectProps: { menuIsOpen, isDisabled },
      getValue,
      getStyles,
    } = props;

    const values = getValue();
    const shouldCollapsePills = isMultiCollapsible && !menuIsOpen && !isDisabled;

    const [visiblePillCount, containerRef] = useDiscreteOverflow(values, {
      isEnabled: shouldCollapsePills,
    });

    const containerClass = useMemo(
      () =>
        css({
          ...getStyles('valueContainer', props),
          ...(shouldCollapsePills && { flexWrap: 'nowrap' }),
        }),
      [getStyles, props, shouldCollapsePills],
    );

    const contextValue = useMemo<MultiCollapsibleContextType>(
      () => ({ maxPills: shouldCollapsePills ? visiblePillCount : undefined }),
      [visiblePillCount, shouldCollapsePills],
    );

    return isMultiCollapsible ? (
      <MultiCollapsibleContext.Provider value={contextValue}>
        <Box className={containerClass} ref={containerRef} overflowX="hidden">
          {props.children}
        </Box>
      </MultiCollapsibleContext.Provider>
    ) : (
      <SelectComponents.ValueContainer {...props} />
    );
  };
